import {
  FlexibleConnectedPositionStrategy,
  GlobalPositionStrategy,
  Overlay,
  OverlayRef,
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Input,
} from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { addMonths, subMonths } from 'date-fns';
import { ResponsiveService } from 'src/app/common/services/responsive.service';
import { LocalStorageService } from 'src/app/common/services/localStorage.service';
import { DateRangeService } from './services/date-range.service';
import { DateInfo } from './interfaces/data-range.interface';
import { BACK } from 'src/defaultImages.js';

@Component({
  selector: 'date-range',
  templateUrl: './date-range.component.html',
  styleUrls: ['./date-range.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateRangeComponent implements OnInit, OnDestroy {
  @Input() disableDate!: (date: Date) => boolean;
  @Output() dateRangeSelected = new EventEmitter<{
    startDate: Date | null;
    endDate: Date | null;
  }>();

  @ViewChild('popoverTemplate', { static: true })
  popoverTemplate!: TemplateRef<any>;

  images = { BACK };
  overlayRef!: OverlayRef;
  currentMonth = new Date();
  selectedStartDate: Date | null = null;
  selectedEndDate: Date | null = null;

  private unsubscribe$ = new Subject<void>();
  private subscription: Subscription = new Subscription();

  firstMonthStart: Date;
  secondMonthStart: Date;
  firstMonthDays: DateInfo[];
  secondMonthDays: DateInfo[];
  daysOfWeek: string[] = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
  isMobile: boolean = false;

  constructor(
    private _overlay: Overlay,
    private _viewContainerRef: ViewContainerRef,
    private _responsiveService: ResponsiveService,
    private _translateService: TranslateService,
    private _localStorage: LocalStorageService,
    private _dateRangeService: DateRangeService
  ) {
    this._translateService.use(this._localStorage.get('language') || 'en_US');
  }

  ngOnInit(): void {
    this.checkScreenSize();
    this.setTranslations();
    this.updateDays();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.subscription?.unsubscribe();
    this.disposeOverlay();
  }

  openDatePicker(targetElement: ElementRef): void {
    this.disposeOverlay();

    const positionStrategy = this.getPositionStrategy(targetElement);

    this.overlayRef = this._overlay.create({
      positionStrategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-dark-backdrop',
    });

    const popoverPortal = new TemplatePortal(
      this.popoverTemplate,
      this._viewContainerRef
    );
    this.overlayRef.attach(popoverPortal);

    this.overlayRef
      .backdropClick()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.disposeOverlay();
      });
  }

  selectDate(date: Date): void {
    if (this.isDateDisabled(date)) return;
    if (
      !this.selectedStartDate ||
      (this.selectedEndDate && date < this.selectedEndDate)
    ) {
      this.selectedStartDate = date;
      this.selectedEndDate = null;
    } else if (
      this.selectedStartDate &&
      !this.selectedEndDate &&
      date >= this.selectedStartDate
    ) {
      this.selectedEndDate = date;
    } else {
      this.selectedStartDate = date;
      this.selectedEndDate = null;
    }

    // Update computed days after selection
    this.updateDays();
  }

  mposDateSelected(dateRange: { startDate: Date; endDate: Date }) {
    if (dateRange?.startDate && dateRange?.endDate) {
      this.dateRangeSelected.emit({
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
      });
    }
    this.disposeOverlay();
  }

  cancelSelection(): void {
    this.disposeOverlay();
  }

  applySelection(): void {
    if (this.selectedStartDate && this.selectedEndDate) {
      this.dateRangeSelected.emit({
        startDate: this.selectedStartDate,
        endDate: this.selectedEndDate,
      });
    }
    this.disposeOverlay();
  }

  previousMonth(): void {
    this.currentMonth = subMonths(this.currentMonth, 1);
    this.updateDays();
  }

  nextMonth(): void {
    this.currentMonth = addMonths(this.currentMonth, 1);
    this.updateDays();
  }

  onDateHover(hoveredDate: Date | null): void {
    if (this.selectedStartDate) {
      const startDate = this.selectedStartDate;
      const endDate = hoveredDate;

      const setHoverRange = (day: DateInfo): void => {
        day.isInHoverRange =
          day?.date &&
          day?.date > startDate &&
          day?.date <= endDate &&
          !this.disableDate(day?.date) &&
          !this.selectedEndDate;
      };

      this.firstMonthDays.forEach(setHoverRange);
      this.secondMonthDays.forEach(setHoverRange);
    } else {
      this.clearHoverRange();
    }
  }
  
  clearHoverRange(): void {
    this.firstMonthDays.forEach(this.removeFromHoverRange);
    this.secondMonthDays.forEach(this.removeFromHoverRange);
  }

  trackByDayName(index: number, dayName: string): string {
    return dayName;
  }

  trackByDay(index: number, day: { date: Date | null }): Date | null {
    return day?.date;
  }

  private setTranslations(): void {
    this.subscription.add(
      this._translateService.get('DATE_RANGE').subscribe((translations) => {})
    );
  }

  private checkScreenSize(): void {
    const responsiveSubscription =
      this._responsiveService.mediaBreakpoint$.subscribe((breakPoint) => {
        this.isMobile = breakPoint === 'xs';
      });
    this.subscription.add(responsiveSubscription);
  }

  private getPositionStrategy(
    targetElement: ElementRef
  ): FlexibleConnectedPositionStrategy | GlobalPositionStrategy {
    if (this.isMobile) {
      return this._overlay
        .position()
        .global()
        .centerHorizontally()
        .bottom('0') as GlobalPositionStrategy;
    } else {
      return this._overlay
        .position()
        .flexibleConnectedTo(targetElement)
        .withPositions([
          {
            originX: 'center',
            originY: 'bottom',
            overlayX: 'center',
            overlayY: 'top',
            offsetY: 8,
          },
          {
            originX: 'center',
            originY: 'top',
            overlayX: 'center',
            overlayY: 'bottom',
            offsetY: -8,
          },
        ]) as FlexibleConnectedPositionStrategy;
    }
  }

  // Check if a date is selected
  private isDateSelected(date: Date): boolean {
    return (
      !this.isDateDisabled(date) &&
      this._dateRangeService.isDateSelected(
        date,
        this.selectedStartDate,
        this.selectedEndDate
      )
    );
  }

  // Check if a date is in range
  private isInRange(date: Date): boolean {
    return (
      !this.isDateDisabled(date) &&
      this._dateRangeService.isInRange(
        date,
        this.selectedStartDate,
        this.selectedEndDate
      )
    );
  }

  private updateDays(): void {
    const adjacentMonths = this._dateRangeService.getAdjacentMonths(
      this.currentMonth
    );
    this.firstMonthStart = adjacentMonths.firstMonthStart;
    this.secondMonthStart = adjacentMonths.secondMonthStart;

    this.firstMonthDays = this._dateRangeService
      .getDaysInMonthWithPadding(this.firstMonthStart)
      .map((date) => ({
        date,
        isDisabled: this.isDateDisabled(date),
        isSelected: this.isDateSelected(date),
        isInRange: this.isInRange(date),
        isInHoverRange: false,
        isToday: this._dateRangeService.isToday(date),
      }));

    this.secondMonthDays = this._dateRangeService
      .getDaysInMonthWithPadding(this.secondMonthStart)
      .map((date) => ({
        date,
        isDisabled: this.isDateDisabled(date),
        isSelected: this.isDateSelected(date),
        isInRange: this.isInRange(date),
        isInHoverRange: false,
        isToday: this._dateRangeService.isToday(date),
      }));
  }

  private disposeOverlay(): void {
    this.overlayRef?.dispose();
    this.overlayRef = null!;
  }

  private isDateDisabled(date: Date): boolean {
    return this.disableDate ? this.disableDate(date) : false;
  }

  private removeFromHoverRange(day:DateInfo):void{
    day.isInHoverRange = false;
  }
}
